import React, { useState, useEffect, useCallback } from "react";
import "./OfficeTimings.scss";
import IconButton from "../../../../components/IconButton/IconButton";
import GeneralTable from "../../../../components/Table/GeneralTable";
import ToggleButton from "../../../../components/ToggleButton/ToggleButton";
import WorkingDaysSettings from "./WorkingDaysSettings";
import BranchWorkingDaysCard from "./BranchWorkingDaysCard";
import ShiftForm from "./ShiftForm";
import BulkUpdateModal from "./BulkUpdateModal";
import EmployeeAssignmentModal from "./EmployeeAssignmentModal";
import axiosInstance from "../../../../axiosInstance";
import { toast } from "react-toastify";
import ColoredCheckbox from "../../../../components/ColoredCheckbox/ColoredCheckbox";
import ButtonWithIcon from "../../../../components/ButtonwithIcon/ButtonWithIcon";
import WarningPopup from "../../../../components/WarningPopup/WarningPopup";
import BranchBadges from "./BranchBadges";
import LoadingScreen from "../../../../components/LoadingScreen/LoadingScreen";

const OfficeTimings = ({ markPageAsCompleted }) => {
  const [shifts, setShifts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWorkingDaysForm, setShowWorkingDaysForm] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedShiftIds, setSelectedShiftIds] = useState([]);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingWorkingDay, setEditingWorkingDay] = useState(null);
  const [refreshWorkingDays, setRefreshWorkingDays] = useState(0);

  // New states for bulk operations
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [showEmployeeAssignmentModal, setShowEmployeeAssignmentModal] =
    useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [timingsResponse, branchesResponse] = await Promise.all([
        axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/office-timings`,
        ),
        axiosInstance.get(`${process.env.REACT_APP_API_BASE_URL}/branches`),
        axiosInstance.get(`${process.env.REACT_APP_API_BASE_URL}/workingdays`),
      ]);

      if (timingsResponse.status === 200) {
        markPageAsCompleted("Office Timings");
        if (Array.isArray(timingsResponse.data.data)) {
          const shiftsWithUppercaseStatus = timingsResponse.data.data.map(
            (shift) => ({
              ...shift,
              status: shift.status ? shift.status.toUpperCase() : "DISABLED",
            }),
          );
          setShifts(shiftsWithUppercaseStatus);
        }
      }

      if (branchesResponse.status === 200) {
        if (Array.isArray(branchesResponse.data)) {
          setBranches(branchesResponse.data);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", {
        message: error.message,
        response: error.response
          ? {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data,
            }
          : null,
        request: error.request
          ? {
              url: error.request.responseURL,
              method: error.request.method,
            }
          : null,
      });
    } finally {
      setLoading(false);
    }
  }, [markPageAsCompleted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditWorkingDay = (workingDayData) => {
    setEditingWorkingDay(workingDayData);
    setShowWorkingDaysForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleWorkingDayFormClose = () => {
    setShowWorkingDaysForm(false);
    setEditingWorkingDay(null);
  };

  const handleWorkingDayFormSuccess = () => {
    setShowWorkingDaysForm(false);
    setEditingWorkingDay(null);
    setRefreshWorkingDays((prev) => prev + 1);
    fetchData();
  };

  const toggleOfficeTimingStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "ENABLED" ? "DISABLED" : "ENABLED";
    const endpoint = `${process.env.REACT_APP_API_BASE_URL}/office-timings/${
      newStatus === "ENABLED" ? "enable" : "disable"
    }/${id}`;

    try {
      await axiosInstance.put(endpoint);
      setShifts((prevShifts) =>
        prevShifts.map((shift) =>
          shift.officeTimingId === id ? { ...shift, status: newStatus } : shift,
        ),
      );
    } catch (error) {
      console.error(`Error changing status to ${newStatus}:`, error);
    }
  };

  const handleShiftSelect = (id) => {
    setSelectedShiftIds((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((selectedId) => selectedId !== id)
        : [...prevSelected, id],
    );
  };

  const handleSelectAllShifts = (isChecked) => {
    if (isChecked) {
      setSelectedShiftIds(shifts.map((shift) => shift.officeTimingId));
    } else {
      setSelectedShiftIds([]);
    }
  };

  const isAllSelected =
    shifts.length > 0 && selectedShiftIds.length === shifts.length;

  const handleBulkDeleteClick = () => {
    if (selectedShiftIds.length === 0) {
      toast.error("Please select office timings to delete.");
      return;
    }
    setShowWarningPopup(true);
  };

  const handleBulkDeleteSubmit = async () => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_BASE_URL}/office-timings`,
        selectedShiftIds,
      );

      if (response.status === 200) {
        toast.success("Selected office timings deleted successfully!");
        setSelectedShiftIds([]);
        fetchData();
      } else {
        console.log("Error deleting selected office timings.");
      }
    } catch (error) {
      console.error("Error during bulk delete:", error);
      if (error.response?.status === 409) {
        console.log(
          error.response?.data?.message ||
            "Cannot delete office timings due to existing dependencies.",
        );
      } else {
        console.log("Failed to delete selected office timings.");
      }
    } finally {
      setShowWarningPopup(false);
    }
  };

  // New employee assignment handler
  const handleEmployeeAssignmentClick = () => {
    if (selectedShiftIds.length !== 1) {
      toast.error(
        "Please select exactly one office timing for employee assignment.",
      );
      return;
    }
    setShowEmployeeAssignmentModal(true);
  };

  // Convert 24-hour time to API format
  const convertTimeToApiFormat = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    return {
      hour: hours,
      minute: minutes,
      second: 0,
      nano: 0,
    };
  };

  // Bulk update submit handler - updated to match API specification
  const handleBulkUpdateSubmit = async (updateData) => {
    try {
      const selectedShiftsData = shifts.filter((shift) =>
        selectedShiftIds.includes(shift.officeTimingId),
      );

      const bulkUpdatePayload = selectedShiftsData.map((shift) => {
        // Parse existing time strings to get hour/minute
        const parseTimeString = (timeStr) => {
          if (!timeStr) return { hour: 0, minute: 0 };
          // Handle both "HH:MM AM/PM" and "HH:MM" formats
          const timePart = timeStr.split(" ")[0]; // Get just the time part
          const [hour, minute] = timePart.split(":").map(Number);
          return { hour: hour || 0, minute: minute || 0 };
        };

        const currentShiftForm = parseTimeString(shift.shiftForm);
        const currentShiftTo = parseTimeString(shift.shiftTo);

        return {
          officeTimingId: shift.officeTimingId,
          shiftName: updateData.shiftName || shift.shiftName,
          offTime:
            updateData.offTime !== undefined
              ? parseFloat(updateData.offTime)
              : shift.offTime,
          shiftForm: updateData.shiftForm
            ? convertTimeToApiFormat(updateData.shiftForm)
            : {
                hour: currentShiftForm.hour,
                minute: currentShiftForm.minute,
                second: 0,
                nano: 0,
              },
          shiftTo: updateData.shiftTo
            ? convertTimeToApiFormat(updateData.shiftTo)
            : {
                hour: currentShiftTo.hour,
                minute: currentShiftTo.minute,
                second: 0,
                nano: 0,
              },
          status: updateData.status || shift.status,
          companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
          branchIds:
            updateData.branchIds && updateData.branchIds.length > 0
              ? updateData.branchIds
              : shift.branches?.map((b) => b.branchId) || [],
        };
      });

      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/office-timings/update-multiple`,
        bulkUpdatePayload,
      );

      if (response.status === 200) {
        toast.success(
          `${selectedShiftIds.length} office timings updated successfully!`,
        );
        setSelectedShiftIds([]);
        setShowBulkUpdateModal(false);
        fetchData();
      } else {
        console.log("Error updating selected office timings.");
      }
    } catch (error) {
      console.error("Error during bulk update:", error);
    }
  };

  // Employee assignment submit handler - updated to match API specification
  const handleEmployeeAssignmentSubmit = async (employeeIds) => {
    try {
      // Use Promise.allSettled to handle partial failures gracefully
      const promises = selectedShiftIds.map((officeTimingId) =>
        axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/office-timings/${officeTimingId}/employees`,
          employeeIds,
        ),
      );

      const results = await Promise.allSettled(promises);

      // Check results
      const successCount = results.filter(
        (result) => result.status === "fulfilled",
      ).length;
      const failureCount = results.filter(
        (result) => result.status === "rejected",
      ).length;

      if (successCount > 0) {
        toast.success(
          `Employees assigned to ${successCount} office timing${
            successCount > 1 ? "s" : ""
          } successfully!`,
        );
      }

      if (failureCount > 0) {
        console.error(
          "Some employee assignments failed:",
          results.filter((r) => r.status === "rejected"),
        );
        toast.warning(
          `${failureCount} office timing${
            failureCount > 1 ? "s" : ""
          } failed to update. Please check and try again.`,
        );
      }

      setSelectedShiftIds([]);
      setShowEmployeeAssignmentModal(false);
      fetchData();
    } catch (error) {
      console.error("Error during employee assignment:", error);
    }
  };

  const handleDelete = async (officeTimingId) => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/office-timings/${officeTimingId}`,
      );

      if (response.status === 200) {
        toast.success("Office timing deleted successfully!");
        fetchData();
      } else {
        console.log("Error deleting office timing.");
      }
    } catch (error) {
      console.error("Error deleting office timing:", error);
      if (error.response?.status === 409) {
        console.log(
          error.response?.data?.message ||
            "Cannot delete office timing due to existing dependencies.",
        );
      } else {
        console.log("Error deleting office timing as it is mapped to branches");
      }
    }
  };

  const handleUpdate = (index) => {
    const shiftToEdit = shifts[index];
    setSelectedShift(shiftToEdit);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setShowCreateForm(false);
    setSelectedShift(null);
    fetchData();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setShowCreateForm(false);
    setSelectedShift(null);
  };

  const handleCreateToggle = () => {
    if (showCreateForm) {
      setShowCreateForm(false);
      setSelectedShift(null);
    } else {
      setSelectedShift(null);
      setShowCreateForm(true);
    }
  };

  const columns = [
    {
      header: (
        <div>
          <ColoredCheckbox
            checked={isAllSelected}
            onChange={(e) => handleSelectAllShifts(e.target.checked)}
            color="green"
            name="selectAll"
          />
          <span className="table-header-text">Shift Name</span>
        </div>
      ),
      accessor: "shiftName",
    },
    { header: "Start Time", accessor: "from" },
    { header: "End time", accessor: "to" },
    { header: "Total Working Hours", accessor: "totalBreakHours" },
    { header: "Branch", accessor: "branch" },
    { header: "Status", accessor: "status" },
    { header: "Actions", accessor: "actions" },
    { header: "", accessor: "searchableText", hidden: true },
  ];

  const tableData = shifts.map((shift, index) => {
    const shiftNameValue = shift.shiftName || shift.name || "Unnamed Shift";

    return {
      id: shift.officeTimingId,
      shiftName: (
        <div className="shift-name-cell">
          <ColoredCheckbox
            checked={selectedShiftIds.includes(shift.officeTimingId)}
            onChange={(e) => handleShiftSelect(shift.officeTimingId)}
            color="green"
            name={`shift-${shift.officeTimingId}`}
          />
          <span className="shift-name-text">{shiftNameValue}</span>
        </div>
      ),
      from: <span className="time-cell">{shift.shiftForm}</span>,
      to: <span className="time-cell">{shift.shiftTo}</span>,
      totalBreakHours: <span className="break-time-cell">{shift.offTime}</span>,
      branch: <BranchBadges branches={shift.branches} />,
      status: (
        <ToggleButton
          isOn={shift.status === "ENABLED"}
          onToggle={() =>
            toggleOfficeTimingStatus(shift.officeTimingId, shift.status)
          }
          status={shift.status === "DISABLED"}
        />
      ),
      actions: (
        <div className="action-buttons">
          <IconButton
            icon="iconamoon:edit-light"
            onClick={() => handleUpdate(index)}
            variant="edit"
            title="Edit Timing"
          />
          <IconButton
            icon="fluent:delete-28-regular"
            onClick={() => handleDelete(shift.officeTimingId)}
            variant="delete"
            title="Delete Timing"
          />
        </div>
      ),
      searchableText: `${shiftNameValue} ${shift.shiftForm} ${shift.shiftTo} ${shift.offTime} ${shift.status}`,
    };
  });

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="office-timings-container">
      <div className="page-header">
        <h1 className="display6">Office Timings</h1>
      </div>

      <div className="action-container">
        <button
          className="create-role-button"
          onClick={() => setShowWorkingDaysForm((prev) => !prev)}
          style={
            showWorkingDaysForm
              ? { backgroundColor: "#ff6692", color: "#fff" }
              : {}
          }
        >
          {!showWorkingDaysForm && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
          )}
          {showWorkingDaysForm ? "Close Working Days" : "Create Working Days"}
        </button>

        <button
          className="create-role-button"
          onClick={handleCreateToggle}
          style={
            showCreateForm ? { backgroundColor: "#ff6692", color: "#fff" } : {}
          }
        >
          {!showCreateForm && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
          )}
          {showCreateForm ? "Close Office Timing Form" : "Create Office Timing"}
        </button>

        {/* Employee Assignment Button */}
        <ButtonWithIcon
          icon="mdi:account-group"
          text={`Assign Employees (${selectedShiftIds.length})`}
          onClick={handleEmployeeAssignmentClick}
          className={`secondary-button ${
            selectedShiftIds.length !== 1 ? "disabled-btn" : ""
          }`}
          disabled={selectedShiftIds.length !== 1}
        />

        <ButtonWithIcon
          icon="fluent:delete-28-regular"
          text={`Delete Selected (${selectedShiftIds.length})`}
          onClick={handleBulkDeleteClick}
          className={`cancel-button ${
            selectedShiftIds.length === 0 ? "disabled-btn" : ""
          }`}
          disabled={selectedShiftIds.length === 0}
        />
      </div>

      <div className="content-section">
        {(showForm || showCreateForm) && (
          <div className="form-container">
            <ShiftForm
              branches={branches}
              selectedShift={selectedShift}
              onCancel={handleFormCancel}
              onSuccess={handleFormSuccess}
            />
          </div>
        )}

        {showWorkingDaysForm && (
          <div className="form-container">
            <WorkingDaysSettings
              editingData={editingWorkingDay}
              onCancel={handleWorkingDayFormClose}
              onSuccess={handleWorkingDayFormSuccess}
            />
          </div>
        )}

        <GeneralTable
          columns={columns}
          data={tableData}
          title="Office Timings"
          selectedCount={selectedShiftIds.length}
        />

        <div className="section-divider">
          <h3 className="section-title">Department Working Days</h3>
        </div>

        <BranchWorkingDaysCard
          onEditWorkingDay={handleEditWorkingDay}
          refreshTrigger={refreshWorkingDays}
        />
      </div>

      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onDecline={() => setShowWarningPopup(false)}
        onAccept={handleBulkDeleteSubmit}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete ${selectedShiftIds.length} office timing(s)? This action cannot be undone.`}
        acceptText="Yes, Delete"
        declineText="Cancel"
      />

      {/* Bulk Update Modal */}
      <BulkUpdateModal
        isOpen={showBulkUpdateModal}
        onClose={() => setShowBulkUpdateModal(false)}
        onSubmit={handleBulkUpdateSubmit}
        selectedCount={selectedShiftIds.length}
        selectedShifts={shifts.filter((shift) =>
          selectedShiftIds.includes(shift.officeTimingId),
        )}
        branches={branches}
      />

      {/* Employee Assignment Modal */}
      <EmployeeAssignmentModal
        isOpen={showEmployeeAssignmentModal}
        onClose={() => setShowEmployeeAssignmentModal(false)}
        onSubmit={handleEmployeeAssignmentSubmit}
        selectedCount={selectedShiftIds.length}
        selectedShifts={shifts.filter((shift) =>
          selectedShiftIds.includes(shift.officeTimingId),
        )}
        allShifts={shifts} // 👈 pass full list here
      />
    </div>
  );
};

export default OfficeTimings;
